import {useMutation} from '@apollo/client'
import {List, Map} from 'immutable'
import PropTypes from 'prop-types'
import QRCode from 'react-qr-code'

import IconButton from '@mui/material/IconButton'

import {Box} from '../../../../../dashboard/src/components/blocks/Boxes'
import Loader from '../../../../../shared_components/NewLoader'
import {QuestionMarkIcon} from './QuestionMarkIcon'
import TfaWizardStepFrame from './TfaWizardStepFrame'

import copyIcon from '../../../../../shared_assets/copyIcon.png'
import {useNotificationContext} from '../../../../../shared_components/notifications/NotificationContext'
import {parseProvisioningUri} from '../../../../../lib/tfaTools'
import {DELETE_TFA_ENROLLMENT} from '../../../../../graphql/mutations/tfa'
import {TFA_ENROLLMENTS} from '../../../../../graphql/queries/tfa'

import './appEnrollment.scss'

const AppEnrollment = props => {
  const [deleteTfaEnrollment] = useMutation(DELETE_TFA_ENROLLMENT, {awaitRefetchQueries: true, refetchQueries: [{query: TFA_ENROLLMENTS}]})
  const {alwaysNotify} = useNotificationContext()
  const provisioningUri = parseProvisioningUri(props.tfaEnrollment.get('provisioning-uri', ''))

  const goBack = () => {
    if (!props.tfaEnrollment.isEmpty() && !props.tfaEnrollment.get('enabled-at'))
      deleteTfaEnrollment({variables: {id: props.tfaEnrollment.get('id')}})

    props.nextStep('START')
  }

  const copySecret = async event => {
    if (event) event.preventDefault()

    if (!(navigator.clipboard && navigator.clipboard.writeText))
      return

    await navigator.clipboard.writeText(provisioningUri.searchParams.get('secret'))
    alwaysNotify.shortSuccess('Authenticator app secret copied.')
  }

  const goToConfirmCode = () => {
    props.logEvent('scanned_tfa_setup_key')
    props.nextStep('CONFIRM')
  }

  return (
    <Loader loading={props.tfaEnrollment.isEmpty()}>
      <TfaWizardStepFrame
        currentStep={2}
        onPrimaryAction={goToConfirmCode}
        secondaryDisabled={props.appEnrollmentOnly}
        onSecondaryAction={goBack}
        primaryActionLabel='I have scanned the code'
        enrollmentMode='app'
        secondaryActionLabel='Back'
        subtitle='Scan the QR code with your authenticator app'
        title='Set up authenticator app'>
        <div className='app-enrollment'>
          <div className='authenticator-setup flex-container'>
            <Box className='instructions'>
              <h4>Scan QR code</h4>
              <ol className='ordered-list'>
                <li>Open your authenticator app</li>
                <li>Tap "add account" or the + icon</li>
                <li>Scan this QR code</li>
              </ol>
              <div className='prompt-container'>
                <QuestionMarkIcon className='prompt-icon' />
                <div className='prompt'>
                  <span className='question'>Don't have an authenticator app?</span>{' '}
                  Download one of the following recommended apps from your
                  device's App Store:{' '}
                  <strong>Google Authenticator, Authy, 1Password</strong>
                </div>
              </div>
            </Box>
            <div className='qr-code'>
              <QRCode
                size={156}
                style={{height: 'auto', maxWidth: '100%', width: '100%'}}
                value={props.tfaEnrollment.get('provisioning-uri', '')}
                viewBox='0 0 256 256'
              />
            </div>
          </div>
          <div className='alternate-setup flex-container'>
            <Box className='instructions'>
              <h4>Or enter setup key manually</h4>
              <div>If you can't scan the QR code, enter this key in your authenticator app:</div>
            </Box>
            <div className='setup-secret flex-container'>
              <div className='secret-code'>
                {provisioningUri.searchParams.get('secret')}
              </div>
              <IconButton onClick={copySecret} aria-label='Copy setup key'>
                <img alt='' aria-hidden='true' src={copyIcon} />
              </IconButton>
            </div>
          </div>
        </div>
      </TfaWizardStepFrame>
    </Loader>
  )
}

AppEnrollment.propTypes = {
  appEnrollmentOnly: PropTypes.bool,
  enrollmentMode: PropTypes.string,
  logEvent: PropTypes.func,
  nextStep: PropTypes.func,
  tfaEnrollment: PropTypes.instanceOf(Map),
  tfaEnrollments: PropTypes.instanceOf(List)
}

export default AppEnrollment
